import {
    AddReservationRequestDto,
    AutoCompleteResponse,
    HotelInfoRequestDto,
    HotelRatesCalendarRequestDto,
    HotelRatesRequestDto,
    HotelSearchAutoCompleteDto,
    HotelSearchRecommendedDto,
    IResponse,
    IRocketTokens,
    ProfileDto,
    ProtoUser,
    RequestAccessDto,
    SERPRegionRequestDto,
    SPBookingFormRequestDto,
    SPBookingFormResponseDto,
    SPHotelInfo,
    SPHotelInfoShort,
    SPHotelRateCalendar,
    SPHotelRates,
    SPPrebookRequestDto,
    SPPrebookResponseDto,
    SPSearchResp,
    UpdateUserDTO,
} from '@eventr/common'
import { AxiosRequestConfig } from 'axios'
import { LngLat } from 'maplibre-gl'
import { requestsItinerary } from './api'
import { gwApiClient } from './apiClients'

interface CacheEntry<T> {
    timestamp: number
    data: T
}

const cache: { [key: string]: CacheEntry<AutoCompleteResponse> } = {}

const CACHE_DURATION = 30 * 60 * 1000 // 30 minutes in milliseconds

function isCacheValid(timestamp: number): boolean {
    return Date.now() - timestamp < CACHE_DURATION
}

export const EventrGatewayAPI = {
    profile: {
        getMe: async (
            config?: AxiosRequestConfig<any>
        ): Promise<IResponse<ProfileDto>> => {
            return gwApiClient.get('profile/me', {
                ...config,
            })
        },
        update: async (body: UpdateUserDTO): Promise<IResponse<ProfileDto>> => {
            return gwApiClient.post('profile', body)
        },
        delete: async (): Promise<IResponse<any>> => {
            return gwApiClient.post('/profile/deleteRequest', {})
        },
        forgotPassword: async (email: string): Promise<IResponse<any>> => {
            return gwApiClient.post('/profile/forgotPassword', { email })
        },
        exists: async (email: string): Promise<IResponse<boolean>> => {
            return gwApiClient.get(`/profile/exists/${email}`)
        },
        createUser: async (body: ProtoUser): Promise<IResponse<any>> => {
            return gwApiClient.post('profile/createUser', body)
        },
        removeMember: async (
            wpUserId: number,
            orgID: string
        ): Promise<IResponse<string>> => {
            return gwApiClient.post(
                `profile/removeOrg/${wpUserId}?orgId=${orgID}`,
                {}
            )
        },
        profileShare: {
            fetchAdditionalInfoFields: async (): Promise<IResponse<any>> => {
                return requestsItinerary.get<any>('config/additionalUserInfo', {
                    NOT_IN_GUEST: true,
                })
            },
        },
    },
    organisation: {
        upgradeTier: async (tier: string): Promise<IResponse<string>> => {
            return gwApiClient.post<{ tier: string }, string>(
                '/org/upgradeTier',
                { tier }
            )
        },
    },
    orgs: {
        getCodeInfo: async (
            code: string,
            populated = false
        ): Promise<IResponse<any>> => {
            return gwApiClient.get(`orgsService/${code}/${populated}`)
        },
        getShareCode: async (
            code: string,
            orgID: string
        ): Promise<IResponse<any>> => {
            return gwApiClient.get(`shareCodeInfo/${code}/${orgID}`)
        },
    },
    booking: {
        hotel: {
            autocomplete: async (
                body: HotelSearchAutoCompleteDto
            ): Promise<IResponse<AutoCompleteResponse>> => {
                const cacheKey = JSON.stringify(body)

                // Check if the cache is valid
                if (
                    cache[cacheKey] &&
                    isCacheValid(cache[cacheKey].timestamp)
                ) {
                    return { data: cache[cacheKey].data, status: 'success' }
                }

                // Make the API call if cache is not valid
                const response = await gwApiClient.post<
                    HotelSearchAutoCompleteDto,
                    AutoCompleteResponse
                >(`/booking/hotel/autocomplete`, body)

                if (response.status === 'success') {
                    // Store the result in the cache
                    cache[cacheKey] = {
                        timestamp: Date.now(),
                        data: response.data!,
                    }
                }

                return response
            },
            SERPregion: async (
                body: SERPRegionRequestDto & { point?: [number, number] }
            ): Promise<IResponse<SPSearchResp>> => {
                return gwApiClient.post<SERPRegionRequestDto, SPSearchResp>(
                    `/booking/hotel/search/serp/region`,
                    body,
                    { timeout: 60000 } // this may take a while
                )
            },
            hotelSearchRecommended: async (
                body: HotelSearchRecommendedDto
            ): Promise<IResponse<SPHotelInfoShort[]>> => {
                return gwApiClient.post<
                    HotelSearchRecommendedDto,
                    SPHotelInfoShort[]
                >(`/booking/hotel/search/recommended`, body, { timeout: 20000 })
            },
            hotelInfo: async (
                body: HotelInfoRequestDto
            ): Promise<IResponse<SPHotelInfo>> => {
                return gwApiClient.post<HotelInfoRequestDto, SPHotelInfo>(
                    `/booking/hotel/info`,
                    body,
                    { timeout: 20000 }
                )
            },
            hotelRates: async (
                body: HotelRatesRequestDto
            ): Promise<IResponse<SPHotelRates>> => {
                return gwApiClient.post<HotelRatesRequestDto, SPHotelRates>(
                    `/booking/hotel/rates`,
                    body,
                    { timeout: 60000 }
                )
            },
            hotelRatesCalendar: async (
                body: HotelRatesCalendarRequestDto
            ): Promise<IResponse<SPHotelRateCalendar>> => {
                return gwApiClient.post<
                    HotelRatesCalendarRequestDto,
                    SPHotelRateCalendar
                >(`/booking/hotel/ratecalendar`, body, { timeout: 30000 })
            },
            prebook: async (
                body: SPPrebookRequestDto
            ): Promise<IResponse<SPPrebookResponseDto>> => {
                return gwApiClient.post<
                    SPPrebookRequestDto,
                    SPPrebookResponseDto
                >(`/booking/hotel/prebook`, body)
            },
            bookForm: async (
                body: SPBookingFormRequestDto
            ): Promise<IResponse<SPBookingFormResponseDto>> => {
                return gwApiClient.post<
                    SPBookingFormRequestDto,
                    SPBookingFormResponseDto
                >(`/booking/hotel/bookform`, body)
            },
            addReservation: async (
                body: AddReservationRequestDto
            ): Promise<IResponse<any>> => {
                return gwApiClient.post<AddReservationRequestDto, any>(
                    `/booking/hotel/addReservation`,
                    body
                )
            },
        },
    },
    utils: {
        setCache: async (cacheValue: any): Promise<IResponse<any>> => {
            return gwApiClient.post('/utils/cache', cacheValue)
        },
    },
    address: {
        findAddress: async (address: string): Promise<IResponse<any>> => {
            return gwApiClient.get(`/addresslookup/v2?q=${address}&lang=en`)
        },
        reverse: async (lngLat: LngLat): Promise<IResponse<any>> => {
            const url = `/addresslookup/v2?reverse=true&lat=${lngLat.lat}&lon=${lngLat.lng}&format=json&namedetails=1`
            return gwApiClient.get(url)
        },
    },
    rocketchat: {
        getRocketchatToken: async (): Promise<IResponse<IRocketTokens[]>> => {
            return gwApiClient.get('/rocketchat/tokens', { NOT_IN_GUEST: true })
        },
        downloadFile: async (
            url: string,
            userId: string,
            token: string
        ): Promise<IResponse<any>> => {
            return gwApiClient.get('rocketchat/downloadFile', {
                params: {
                    userId: userId,
                    token: token,
                    fileUrl: url,
                },
            })
        },
        ensureRunning: async (orgID: string): Promise<IResponse<any>> => {
            return gwApiClient.get(`/rocketchat/ensureRunning?orgId=${orgID}`, {
                NOT_IN_GUEST: true,
            })
        },
    },
    media: {
        upload: async (
            formData: FormData,
            org?: string
        ): Promise<IResponse<any>> => {
            return gwApiClient.post(`media?orgId=${org}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 40000,
            })
        },
    },
    requestAccess: {
        get: async (): Promise<IResponse<any>> => {
            return gwApiClient.get('/requestAccess')
        },
        approveRequest: async (
            requestID: string,
            org: string
        ): Promise<IResponse<any>> => {
            return gwApiClient.post(
                `/requestAccess/approve/${requestID}?orgId=${org}`,
                {}
            )
        },
        rejectRequest: async (
            requestID: string,
            org: string,
            reason: string
        ): Promise<IResponse<any>> => {
            return gwApiClient.post(
                `/requestAccess/reject/${requestID}?orgId=${org}`,
                { reason }
            )
        },
        request: async (body: RequestAccessDto): Promise<IResponse<any>> => {
            return gwApiClient.post(`/requestAccess`, body)
        },
        requestItinAccess: async (
            body: RequestAccessDto
        ): Promise<IResponse<any>> => {
            return gwApiClient.post(`/requestItineraryAccess`, body)
        },
    },
    registration: {
        confirmInvite: async (
            orgID: string,
            inviteToken: string
        ): Promise<IResponse<any>> => {
            return gwApiClient.post(
                `invite/confirm/${inviteToken}?orgId=${orgID}`,
                {}
            )
        },
    },
}
